import axios from 'axios';
import { ProductCategoryDto } from '../../model/dto/productCategoryDto';
import { ProductCategoryDetailsDto } from '../../model/dto/productCategoryDetailsDto';
import { ProductChatRequestDto } from '../../model/dto/productChatRequestDto';
import { ProductLineDto } from '../../model/dto/productLineDto';
import { ProductLineDetailsDto } from '../../model/dto/productLineDetailsDto';
import { ProductLineOnboardingDto } from '../../model/dto/productLineOnboardingDto';
import { ProductRelevantArticlesDto } from '../../model/dto/productRelevantArticlesDto';
import { SanityDocumentDto } from '../../model/dto/sanityDocumentDto';
import { BaseApi } from './BaseApi';

class ProductApi extends BaseApi {

  GetCategories = async (): Promise<ProductCategoryDto[]> => {
    return this.GetCached('Categories', 'all', () =>
      axios.get(`${process.env.REACT_APP_API_URL}/Products/Categories`)
        .then((response) => {
          if (response.status === 200) return response.data;
        })
    );
  };

  GetProductLinesWithDocuments = async (): Promise<ProductLineDto[]> => {
    return this.GetCached('ProductLinesWithDocuments', 'all', () =>
      axios.get(`${process.env.REACT_APP_API_URL}/Products/ProductLinesWithDocuments`)
        .then((response) => {
          if (response.status === 200) return response.data;
        })
    );
  };

  GetDocumentsByProductLineCode = async (code: string): Promise<SanityDocumentDto[]> => {
    return this.GetCached('DocumentsByProductLineCode', code, () =>
      axios.get(`${process.env.REACT_APP_API_URL}/Products/ProductLines/Documents?code=${encodeURIComponent(code)}`)
        .then((response) => {
          if (response.status === 200) return response.data;
        })
    );
  };

  GetProductLineCodeByAgileCode = async (agileCode: string): Promise<string> => {
    return this.GetCached('ProductLineCodeByAgileCode', agileCode, () =>
      axios.get(`${process.env.REACT_APP_API_URL}/Products/ProductLines/AgileCode?supportProductLineName=${encodeURIComponent(agileCode)}`)
        .then((response) => {
          if (response.status === 200) return response.data;
        })
    );
  };

  GetProductCategories = async (): Promise<ProductCategoryDto[]> => {
    return this.GetCached('ProductCategories', 'all', () =>
      axios.get(`${process.env.REACT_APP_API_URL}/Products/ProductCategories`)
        .then((response) => {
          if (response.status === 200) return response.data;
        })
    );
  };

  GetProductCategory = async (linkName: string, lang?: string | undefined, signal?: AbortSignal): Promise<ProductCategoryDetailsDto> => {
    const cacheKey = `${linkName}|${lang ?? ''}`;

    return this.GetCached('ProductCategory', cacheKey, () => {
      let url = `${process.env.REACT_APP_API_URL}/Products/ProductCategory?linkName=${linkName}`;
      if (!!lang) url += `&lang=${lang}`;
      return axios.get(url, { signal: signal }).then((response) => {
        if (response.status === 200) return response.data;
      });
    });
  };

  GetProductLine = async (productLink: string, lang?: string | undefined, signal?: AbortSignal, useCache?: boolean): Promise<ProductLineDetailsDto> => {
    const cacheKey = `${productLink}|${lang ?? ''}`;

    const fetch = () => {
      let url = `${process.env.REACT_APP_API_URL}/Products/ProductLine?productLink=${encodeURIComponent(productLink)}`;
      if (!!lang) url += `&lang=${lang}`;
      return axios.get(url, { signal: signal }).then((response) => {
        if (response.status === 200) return response.data;
      });
    };

    if (useCache === false) 
      return fetch();

    return this.GetCached('ProductLine', cacheKey, fetch);
  };

  GetProductLineOnboardingDetails = async (productLink: string, lang?: string | undefined, signal?: AbortSignal): Promise<ProductLineOnboardingDto> => {
    const cacheKey = `${productLink}|${lang ?? ''}`;

    return this.GetCached('ProductLineOnboarding', cacheKey, () => {
      let url = `${process.env.REACT_APP_API_URL}/Products/ProductLineOnboarding?productLink=${encodeURIComponent(productLink)}`;
      if (!!lang) url += `&lang=${lang}`;
      return axios.get(url, { signal: signal }).then((response) => {
        if (response.status === 200) return response.data;
      });
    });
  };

  GetRelevantArticlesLine = async (categoryLink: string, productLink: string, componentLink: string, lang?: string | undefined, signal?: AbortSignal): Promise<ProductRelevantArticlesDto> => {
    const cacheKey = `${categoryLink}|${productLink}|${componentLink}|${lang ?? ''}`;

    return this.GetCached('RelevantArticlesLine', cacheKey, () => {
      let url = `${process.env.REACT_APP_API_URL}/Products/RelevantArticlesLine?categoryLink=${categoryLink}&productLink=${encodeURIComponent(productLink)}&componentLink=${encodeURIComponent(componentLink)}`;
      if (!!lang) url += `&lang=${lang}`;
      return axios.get(url, { signal: signal }).then((response) => {
        if (response.status === 200) return response.data;
      });
    });
  };

  ChatStream = async (
    request: ProductChatRequestDto,
    onToken: (token: string) => void,
    signal?: AbortSignal
  ): Promise<void> => {
    const response = await fetch(`${process.env.REACT_APP_API_URL}/Products/ChatStream`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(request),
      signal,
    });

    if (!response.ok) {
      throw new Error(`Chat request failed: ${response.status}`);
    }

    const reader = response.body?.getReader();
    if (!reader) throw new Error('No response body');

    const decoder = new TextDecoder();
    let buffer = '';

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() || '';

      for (const line of lines) {
        if (line.startsWith('data: ')) {
          const data = line.slice(6).trim();
          if (data === '[DONE]') return;
          try {
            const parsed = JSON.parse(data);
            if (parsed.token) {
              onToken(parsed.token);
            }
            if (parsed.error) {
              throw new Error(parsed.error);
            }
          } catch (e) {
            if (e instanceof SyntaxError) continue;
            throw e;
          }
        }
      }
    }
  };
}

export default new ProductApi;